import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import { useConfirm } from '../../components/ConfirmModal';

const STATUS_TABS = [
  { key: '',          label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'draft',     label: 'Drafts' },
];

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '14px' }}>
    <div className="d-flex align-items-center gap-3">
      <div style={{ width: 48, height: 48, borderRadius: 12, background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
        {icon}
      </div>
      <div>
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
        <div className="fw-bold" style={{ fontSize: 24, color: 'var(--text-main)', lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{sub}</div>}
      </div>
    </div>
  </div>
);

const AdminBlog = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [posts, setPosts]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [page, setPage]         = useState(1);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('q', search);
      if (status) params.set('status', status);
      const [postsRes, analyticsRes] = await Promise.all([
        API.get(`/admin/blog?${params}`),
        API.get('/admin/blog/analytics'),
      ]);
      setPosts(postsRes.data.posts);
      setTotal(postsRes.data.total);
      setAnalytics(analyticsRes.data);
    } catch (_) {}
    setLoading(false);
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  const togglePublish = async (post) => {
    try {
      await API.put(`/admin/blog/${post._id}`, { isPublished: !post.isPublished });
      load();
    } catch (_) {}
  };

  const deletePost = async (id) => {
    const ok = await confirm({ title: 'Delete blog post?', message: 'This post will be permanently deleted and unpublished immediately.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    setDeleting(id);
    try {
      await API.delete(`/admin/blog/${id}`);
      load();
    } catch (_) {}
    setDeleting(null);
  };

  return (
    <AdminLayout title="Blog">
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-0" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>Blog Posts</h4>
          <small className="text-muted">{total} post{total !== 1 ? 's' : ''} total</small>
        </div>
        <Link to="/admin/blog/new" className="btn btn-dream rounded-pill px-4">
          <i className="bi bi-plus-lg me-1"></i>New Post
        </Link>
      </div>

      {/* Analytics summary */}
      {analytics && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <StatCard icon="👁️" label="Total Views" value={analytics.totals.totalViews.toLocaleString()} color="#6f42c1" />
          </div>
          <div className="col-6 col-md-3">
            <StatCard icon="🚀" label="CTA Clicks" value={analytics.totals.totalCtaClicks.toLocaleString()}
              sub="Start Free button clicks" color="#10b981" />
          </div>
          <div className="col-6 col-md-3">
            <StatCard icon="📊" label="Click Rate"
              value={analytics.totals.totalViews > 0
                ? ((analytics.totals.totalCtaClicks / analytics.totals.totalViews) * 100).toFixed(1) + '%'
                : '—'}
              sub="Views → signups" color="#f59e0b" />
          </div>
          <div className="col-6 col-md-3">
            <StatCard icon="📝" label="Published Posts" value={analytics.totals.publishedPosts}
              sub={`of ${analytics.totals.totalPosts} total`} color="#3b82f6" />
          </div>
        </div>
      )}

      {/* Top posts */}
      {analytics && analytics.topByViews.length > 0 && (
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '14px' }}>
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Top Posts by Views</h6>
              {analytics.topByViews.slice(0, 5).map((p, i) => (
                <div key={p._id} className="d-flex align-items-center gap-2 mb-2">
                  <span style={{ width: 20, textAlign: 'center', fontSize: 12, color: 'var(--text-soft)', fontWeight: 700 }}>{i + 1}</span>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div className="text-truncate fw-semibold" style={{ fontSize: 13, color: 'var(--text-main)' }}>{p.title}</div>
                  </div>
                  <span className="badge rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: 12 }}>
                    {p.views.toLocaleString()} views
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="col-md-6">
            <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '14px' }}>
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Top Posts by CTA Clicks</h6>
              {analytics.topByCtr.slice(0, 5).map((p, i) => (
                <div key={p._id} className="d-flex align-items-center gap-2 mb-2">
                  <span style={{ width: 20, textAlign: 'center', fontSize: 12, color: 'var(--text-soft)', fontWeight: 700 }}>{i + 1}</span>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div className="text-truncate fw-semibold" style={{ fontSize: 13, color: 'var(--text-main)' }}>{p.title}</div>
                  </div>
                  <span className="badge rounded-pill" style={{ background: '#d1fae5', color: '#065f46', fontSize: 12 }}>
                    {p.ctaClicks} clicks
                  </span>
                  {p.views > 0 && (
                    <span className="badge rounded-pill" style={{ background: '#fef3c7', color: '#92400e', fontSize: 11 }}>
                      {((p.ctaClicks / p.views) * 100).toFixed(1)}%
                    </span>
                  )}
                </div>
              ))}
              {analytics.topByCtr.length === 0 && (
                <p className="text-muted small mb-0">No CTA clicks yet.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '14px' }}>
        <div className="card-body p-3">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            {/* Status tabs */}
            <div className="btn-group">
              {STATUS_TABS.map(t => (
                <button
                  key={t.key}
                  className={`btn btn-sm ${status === t.key ? 'btn-dream' : 'btn-outline-secondary'}`}
                  onClick={() => { setStatus(t.key); setPage(1); }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} className="d-flex gap-2 ms-auto">
              <input
                className="form-control form-control-sm"
                placeholder="Search title or tag…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ minWidth: 220 }}
              />
              <button className="btn btn-sm btn-outline-purple" type="submit">
                <i className="bi bi-search"></i>
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: '14px', overflow: 'hidden' }}>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-purple" style={{ color: '#6f42c1' }}></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-journal-x fs-1 d-block mb-2"></i>
            No posts found.{' '}
            <Link to="/admin/blog/new" className="text-purple fw-bold">Create your first post</Link>
          </div>
        ) : (
          <table className="table table-hover mb-0">
            <thead style={{ background: 'var(--page-bg)', fontSize: '12px', textTransform: 'uppercase' }}>
              <tr>
                <th className="ps-4">Title</th>
                <th>Tags</th>
                <th>Status</th>
                <th>Views</th>
                <th>CTA</th>
                <th>CTR</th>
                <th>Date</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post._id}>
                  <td className="ps-4 align-middle" style={{ maxWidth: 300 }}>
                    <div className="fw-semibold text-truncate" style={{ color: 'var(--text-main)' }}>{post.title}</div>
                    <small className="text-muted">/blog/{post.slug}</small>
                  </td>
                  <td className="align-middle">
                    {post.tags.slice(0, 3).map(t => (
                      <span key={t} className="badge bg-purple-light text-purple me-1" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>{t}</span>
                    ))}
                  </td>
                  <td className="align-middle">
                    <span className={`badge rounded-pill ${post.isPublished ? 'bg-success' : 'bg-secondary'}`}>
                      {post.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="align-middle" style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>
                    {(post.views || 0).toLocaleString()}
                  </td>
                  <td className="align-middle" style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>
                    {post.ctaClicks || 0}
                  </td>
                  <td className="align-middle" style={{ fontSize: '13px' }}>
                    {post.views > 0
                      ? <span style={{ color: '#059669', fontWeight: 600 }}>{((post.ctaClicks / post.views) * 100).toFixed(1)}%</span>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td className="align-middle" style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>
                    {post.isPublished && post.publishedAt
                      ? new Date(post.publishedAt).toLocaleDateString()
                      : new Date(post.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="align-middle text-end pe-4">
                    <div className="d-flex gap-2 justify-content-end">
                      <button
                        className={`btn btn-sm rounded-pill ${post.isPublished ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                        onClick={() => togglePublish(post)}
                        title={post.isPublished ? 'Unpublish' : 'Publish'}
                        style={{ fontSize: '12px' }}
                      >
                        <i className={`bi ${post.isPublished ? 'bi-eye-slash' : 'bi-send'}`}></i>
                        {' '}{post.isPublished ? 'Unpublish' : 'Publish'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-purple rounded-pill"
                        onClick={() => navigate(`/admin/blog/${post._id}/edit`)}
                        style={{ fontSize: '12px' }}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger rounded-pill"
                        onClick={() => deletePost(post._id)}
                        disabled={deleting === post._id}
                        style={{ fontSize: '12px' }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="d-flex justify-content-center mt-4 gap-2">
          <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <span className="align-self-center small text-muted">Page {page} of {Math.ceil(total / 20)}</span>
          <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBlog;
